import PropTypes from 'prop-types'
import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { MATCHINGPROFILE } from '../../constants/evaluation-types'
import { getJobCategory } from '../../constants/job-categories'
import trackEvents from '../../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  useNotificationContext,
  usePaymentContext
} from '../../context'
import { useFeatureGate } from '../../features/pricing/use-feature-gate'
import { query } from '../../graphql'
import { classNames } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { openPdfLoadingWindow } from '../../helpers/pdf-loading'
import { sortCrossComparison } from '../../helpers/sortings'
import Divider from '../divider'
import LockedIcon from '../icons/locked-icon'
import Paywall from '../paywall'
import LockBadge from '../pricing/lock-badge'
import UpgradeInline from '../pricing/upgrade-inline'
import Table from '../react-table/table'
import ReinviteSpaceModal from '../shared/match-me-if-you-can/reinvite-space-modal'
import Button from '../tailwind/Button'
import Panel from '../tailwind/Panel'
import Toggle from '../tailwind/toggle'
// import ReinviteSpaceModal from '../talents/reinvite-space-modal'
import Tooltip from '../tooltip'
import VersionSwitch from '../version-switch'

const TRANSLATION_LOCATION = 'pages.talent.'

const CROSS_MATCHING_FEATURE = 'CROSS_MATCHING'
const CROSS_MATCHING_SOURCE = 'talent_cross_matching'

const {
  CLICK_REQUEST_CHALLENGES_TALENT_DETAILS,
  CAREERS_MATCHING_SHOW_DIAGNOSTIC_VERSION_1,
  CAREERS_MATCHING_SHOW_DIAGNOSTIC_VERSION_2
} = trackEvents

const CareersMatching = ({
  space,
  refetchSpace,
  data,
  showArchivedCareers,
  setShowArchivedCareers
}) => {
  const { t } = useTranslation()
  const { error } = useNotificationContext()
  const { language } = useLanguageContext()
  const { crossComparison, showPaywall } = usePaymentContext()
  const { system } = useAivyContext()
  // Top 5 und Platzhalter 'blur' bleiben in jedem Modus. In enforced kommen
  // statt Schloss und statischer Paywall die Pille und die Upgrade-Karte.
  const crossMatchingGate = useFeatureGate(CROSS_MATCHING_FEATURE, {
    source: CROSS_MATCHING_SOURCE
  })

  const matchMeIfYouCan = data || []

  const hasV1Careers = matchMeIfYouCan.some(
    ({ diagnostic_version }) => diagnostic_version === 1
  )

  const hasV2Careers = matchMeIfYouCan.some(
    ({ diagnostic_version }) => diagnostic_version === 2
  )

  // switch is only displayed if both versions are available
  const [activeDiagnosticVersion, setActiveDiagnosticVersion] = useState(2)
  const [showReinviteSpaceModal, setShowReinviteSpaceModal] = useState(false)
  const [missingChallengesCache, setMissingChallengesCache] = useState({})

  // Helper function to get missing challenges for a career
  const getMissingChallengesForCareer = async (careerId) => {
    try {
      // Get the career configuration
      const careerResponse = await query({
        query: 'getCareer',
        variables: { id: careerId }
      })

      if (!careerResponse?.app_settings?.challenges) return []

      // Get required challenges for this career
      const requiredChallenges = JSON.parse(
        careerResponse.app_settings.challenges
      ).map((c) => c.exam_id)

      // Get completed challenges for this talent
      const answersResponse = await query({
        query: 'answersBySpace',
        variables: { space_id: space.id }
      })

      const completedChallenges = answersResponse.map(
        (answer) => answer.exam_id
      )

      // Find missing challenges
      const missingChallengeIds = requiredChallenges.filter(
        (challengeId) => !completedChallenges.includes(challengeId)
      )

      // Convert to localized names using system challenge data
      const systemChallenges = system?.challenges
        ? JSON.parse(system.challenges.data)
        : null
      if (systemChallenges) {
        return missingChallengeIds.map((examId) => {
          const challenge = systemChallenges[examId]
          if (challenge && challenge.title && challenge.title[language]) {
            return challenge.title[language]
          }
          return examId
        })
      }

      return missingChallengeIds
    } catch (error) {
      console.error('Error getting missing challenges:', error)
      return []
    }
  }

  const generatePDF = ({ career_id, diagnostic_version }) => {
    const variables = { career_id, language }

    if (diagnostic_version === 2) {
      variables.space_id = space.id
      variables.type = MATCHINGPROFILE
    }

    const { partner_user_id, owner } = space
    if (partner_user_id) variables.partner_user_id = partner_user_id
    else variables.user_id = owner

    const pdfDocument = openPdfLoadingWindow({ t, language })

    query({ query: 'generatePdf', variables })
      .then((response) => {
        pdfDocument.location.href = JSON.parse(response).headers.Location
        trackEvent(trackEvents.VIEW_MATCHING_PROFILE_CROSSCOMPARISON, {
          diagnostic_version
        })
      })
      .catch((err) => {
        error()
        console.error(err)
        pdfDocument.close()
      })
  }

  const columns = [
    {
      accessorKey: 'title',
      enableSorting: crossComparison,
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('pages.talent.careers_matching_careers_table_column_careers')}
        </span>
      ),
      cell: ({ cell }) => {
        const { title, external_custom_id, score, archived } = cell.row.original

        const career_title = external_custom_id
          ? `${title} | ${external_custom_id}`
          : title

        return (
          <div className='flex items-center space-x-3'>
            <div
              className='size-3 flex-shrink-0 rounded-full'
              aria-hidden='true'
              style={{ backgroundColor: score.color }}
            />
            <div className='flex flex-col'>
              <span
                className={classNames(
                  'text-sm font-medium',
                  archived ? 'text-gray-500' : 'text-gray-900'
                )}
              >
                {career_title}
              </span>
              {archived && (
                <span className='text-xs italic text-gray-400'>
                  {t(TRANSLATION_LOCATION + 'careers_matching_archived_label')}
                </span>
              )}
            </div>
          </div>
        )
      }
    },
    {
      accessorKey: 'score',
      enableSorting: crossComparison,
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('pages.talent.careers_matching_careers_table_column_score')}
        </span>
      ),
      sortingFn: sortCrossComparison,
      cell: ({ getValue, cell }) => {
        const { complete_dataset, value } = getValue() || {}
        const { archived, id: careerId } = cell.row.original
        const displayTooltip = !complete_dataset && crossComparison
        const cacheKey = `${space.owner}-${careerId}`

        // Load missing challenges if not cached and tooltip should be displayed
        if (displayTooltip && !missingChallengesCache[cacheKey]) {
          getMissingChallengesForCareer(careerId).then((missing) => {
            setMissingChallengesCache((prev) => ({
              ...prev,
              [cacheKey]: missing
            }))
          })
        }

        // Create enhanced tooltip text
        let tooltipText = t('talents.crossmode.complete_dataset_false_tooltip')
        const missingChallenges = missingChallengesCache[cacheKey] || []
        if (missingChallenges.length > 0) {
          const challengeNames = missingChallenges.join(', ')
          tooltipText += `<br/><br/><strong>${t('talents.crossmode.missing_challenges_label')}:</strong><br/>${challengeNames}`
        }

        return (
          <div className='flex items-center'>
            <span
              className={classNames(
                'text-sm',
                archived ? 'text-gray-400' : 'text-gray-500',
                complete_dataset ? null : 'italic',
                crossComparison ? null : 'blur'
              )}
            >
              {crossComparison ? value : 'blur'}
            </span>
            {displayTooltip && (
              <div className='ml-2'>
                <Tooltip
                  id={`complete_dataset_false_tooltip_${cacheKey}`}
                  place='top'
                  tip={tooltipText}
                />
              </div>
            )}
          </div>
        )
      }
    },
    {
      accessorKey: 'category',
      enableSorting: crossComparison,
      header: (
        <span className='text-sm font-semibold text-gray-900'>
          {t('pages.talent.careers_matching_careers_table_column_category')}
        </span>
      ),
      cell: ({ getValue, cell }) => {
        const { archived } = cell.row.original
        return (
          <span
            className={classNames(
              'text-sm',
              archived ? 'text-gray-400' : 'text-gray-500'
            )}
          >
            {getValue() ? t(getJobCategory(getValue())) : ''}
          </span>
        )
      }
    },
    {
      accessorKey: 'id',
      enableSorting: false,
      header: (
        <span className='ml-auto text-sm font-semibold text-gray-900'>
          {t('pages.talent.careers_matching_careers_table_column_result')}
        </span>
      ),
      cell: ({ cell }) => {
        const {
          id: career_id,
          complete_dataset,
          diagnostic_version,
          archived
        } = cell.row.original

        const available = crossComparison && complete_dataset

        return (
          <span
            className={classNames(
              'flex items-center justify-end text-sm',
              available
                ? archived
                  ? 'cursor-pointer text-gray-500 hover:text-gray-600'
                  : 'cursor-pointer text-primaryBlue hover:text-primaryBlue/80'
                : 'italic',
              !available && (archived ? 'text-gray-400' : 'text-gray-500')
            )}
            onClick={
              available
                ? () => generatePDF({ career_id, diagnostic_version })
                : undefined
            }
          >
            {t(
              TRANSLATION_LOCATION +
                (available
                  ? 'careers_matching_careers_table_column_result_action'
                  : 'careers_matching_careers_table_column_result_not_available')
            )}
          </span>
        )
      }
    }
  ]

  const { all_challenges_unlocked } = space
  const matchings_have_not_complete_dataset = matchMeIfYouCan.some(
    ({ complete_dataset }) => complete_dataset === false
  )

  const alreadyReinvited =
    all_challenges_unlocked && matchings_have_not_complete_dataset

  const showReinviteOption =
    !all_challenges_unlocked && matchings_have_not_complete_dataset

  const filteredMatchMeIfYouCan =
    hasV1Careers && hasV2Careers
      ? matchMeIfYouCan.filter(({ diagnostic_version }) => {
          return diagnostic_version === activeDiagnosticVersion
        })
      : matchMeIfYouCan

  return (
    <>
      <Panel>
        <div className='w-full flex-1'>
          <div className='flex flex-col gap-2 border-b border-gray-300 pb-5 xl:flex-row xl:items-end'>
            <div>
              <div className='flex flex-row items-center'>
                <h2 className='text-lg font-medium leading-6 text-gray-900'>
                  {t(TRANSLATION_LOCATION + 'cross_matching_title')}
                </h2>
                {!crossComparison &&
                  (crossMatchingGate.enforced ? (
                    <LockBadge
                      feature={CROSS_MATCHING_FEATURE}
                      source={CROSS_MATCHING_SOURCE}
                      size='xs'
                      className='ml-2'
                    />
                  ) : (
                    <div
                      className='cursor-pointer'
                      onClick={() => showPaywall('general')}
                    >
                      <LockedIcon />
                    </div>
                  ))}
              </div>
              <p className='mt-1 max-w-2xl text-sm text-gray-500'>
                {t(TRANSLATION_LOCATION + 'cross_matching_description')}
              </p>
            </div>
            <div className='ml-auto flex items-center gap-x-4'>
              {showReinviteOption && (
                <Button.SecondaryBase
                  onClick={() => {
                    trackEvent(CLICK_REQUEST_CHALLENGES_TALENT_DETAILS)
                    setShowReinviteSpaceModal(true)
                  }}
                  text={t('talents.open_reinvite_space_modal_action')}
                />
              )}
              {alreadyReinvited && (
                <span className='text-sm italic text-gray-500'>
                  {t('talents.already_reinvited_hint')}
                </span>
              )}
              <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6'>
                {hasV1Careers && hasV2Careers && (
                  <VersionSwitch
                    versions={[
                      {
                        version: 1,
                        active: activeDiagnosticVersion === 1,
                        handleChange: () => {
                          trackEvent(CAREERS_MATCHING_SHOW_DIAGNOSTIC_VERSION_1)
                          setActiveDiagnosticVersion(1)
                        }
                      },
                      {
                        version: 2,
                        active: activeDiagnosticVersion === 2,
                        handleChange: () => {
                          trackEvent(CAREERS_MATCHING_SHOW_DIAGNOSTIC_VERSION_2)
                          setActiveDiagnosticVersion(2)
                        }
                      }
                    ]}
                  />
                )}
                <Toggle
                  label={t(
                    TRANSLATION_LOCATION + 'careers_matching_show_archived'
                  )}
                  appearance='with-right-label'
                  enabled={showArchivedCareers}
                  setEnabled={setShowArchivedCareers}
                />
              </div>
            </div>
          </div>
          {crossComparison && matchMeIfYouCan.length > 0 && (
            <Table
              className='bg-gray-50'
              classNameTableHeader=''
              columns={columns}
              data={filteredMatchMeIfYouCan}
              initialState={{ sorting: [{ id: 'score', desc: false }] }}
              disableHover
              disableVirtual
              disableFilter
            />
          )}
          {crossComparison && !matchMeIfYouCan.length && (
            <span className='mt-4 block text-sm italic text-gray-500'>
              {t(TRANSLATION_LOCATION + 'careers_matching_no_active_careers')}
            </span>
          )}
          {!crossComparison && matchMeIfYouCan.length > 0 && (
            <>
              <Table
                className='bg-gray-50'
                classNameTableHeader=''
                columns={columns}
                data={filteredMatchMeIfYouCan}
                initialState={{ sorting: [{ id: 'score', desc: false }] }}
                disableHover
                disableVirtual
                disableFilter
              />
              <Divider marginY={32} />
              <div className='mb-2 mt-4'>
                {crossMatchingGate.enforced ? (
                  <UpgradeInline
                    feature={CROSS_MATCHING_FEATURE}
                    source={CROSS_MATCHING_SOURCE}
                  />
                ) : (
                  <Paywall type='static' paywall='general' />
                )}
              </div>
            </>
          )}
          {!crossComparison && !matchMeIfYouCan.length && (
            <div className='mb-2 mt-8'>
              {crossMatchingGate.enforced ? (
                <UpgradeInline
                  feature={CROSS_MATCHING_FEATURE}
                  source={CROSS_MATCHING_SOURCE}
                />
              ) : (
                <Paywall type='static' paywall='general' />
              )}
            </div>
          )}
        </div>
      </Panel>
      <ReinviteSpaceModal
        space={space}
        updateHandler={refetchSpace}
        open={!!showReinviteSpaceModal}
        setOpen={() => setShowReinviteSpaceModal(false)}
      />
    </>
  )
}

CareersMatching.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  showArchivedCareers: PropTypes.bool.isRequired,
  setShowArchivedCareers: PropTypes.func.isRequired
}

export default CareersMatching
